import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Button } from '~/components/common/Button'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { ProgressBar } from '~/components/common/progress-bar/ProgressBar'
import { Typography } from '~/components/common/Typography'
import { CommandType } from '~/constants/commands'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  AgentRequest,
  CodeContext,
  Command,
  IntegrationTestConfig,
  IntegrationTestFlow,
  IntegrationTestInputOutputExample,
  Metadata,
  RuntimeSettings
} from '~/protos/agents'
import {
  completeDeploymentConfiguration,
  completeFlowsConfiguration,
  goToPreviousStep,
  goToStep,
  selectAutoGenerateTestData,
  selectCurrentStep,
  selectCurrentStepNumber,
  selectDeploymentConfig,
  selectFlowRepository,
  selectApplicationSchemas,
  selectSelectedFlows,
  selectAdditionalNotes,
  selectTotalSteps,
  setAutoGenerateTestData,
  updateFlowSelection
} from '~/store/integrationTestFlowSlice'
import { parseRepositoryName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'
import { ConfigureFlowsSlide } from './shared/integration-test/slides/ConfigureFlowsSlide'
import { ConfirmationSlide } from './shared/integration-test/slides/ConfirmationSlide'
import { DeploymentSlide } from './shared/integration-test/slides/DeploymentSlide'
import { TriggerConfirmationModal } from './shared/integration-test/TriggerConfirmationModal'

interface IntegrationTestFlowScreenProps {
  onExit: () => void
  onTaskSubmit?: (taskParams: TaskParams) => Promise<boolean>
}

export function IntegrationTestFlowScreen({
  onExit,
  onTaskSubmit
}: IntegrationTestFlowScreenProps) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { currentWorkspace } = useWorkspaceContext()
  const swiperRef = useRef<SwiperType | null>(null)

  // Get state from Redux
  const currentStep = useSelector(selectCurrentStep)
  const currentStepNumber = useSelector(selectCurrentStepNumber)
  const totalSteps = selectTotalSteps()
  const flowRepository = useSelector(selectFlowRepository)
  const selectedFlows = useSelector(selectSelectedFlows)
  const autoGenerateTestData = useSelector(selectAutoGenerateTestData)
  const applicationSchemas = useSelector(selectApplicationSchemas)
  const deploymentConfig = useSelector(selectDeploymentConfig)
  const additionalNotes = useSelector(selectAdditionalNotes)

  // Modal state for trigger confirmation
  const [showTriggerModal, setShowTriggerModal] = useState(false)

  // Map currentStep to slide index
  const slideIndex = currentStepNumber > 0 ? currentStepNumber - 1 : 0

  // Sync swiper with Redux state
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== slideIndex) {
      swiperRef.current.slideTo(slideIndex)
    }
  }, [slideIndex])

  const handleBack = () => {
    if (currentStepNumber === 1) {
      // First step - go back to repository selection
      dispatch(goToPreviousStep())
      // Navigate back to workspace home if no conversation UUID, otherwise to conversation
      if (uuid) {
        navigate(`/c/${uuid}`)
      } else if (currentWorkspace?.uuid) {
        navigate(`/w/${currentWorkspace.uuid}`)
      }
    } else if (currentStep === 'review') {
      // Confirmation slide - Start Over: reset user-provided data but keep flows selected
      const resetFlows = selectedFlows.map((flow: any) => ({
        ...flow,
        selected: true,
        hasProvidedData: false,
        examples: []
      }))
      dispatch(updateFlowSelection({ flows: resetFlows }))
      dispatch(setAutoGenerateTestData(true))
      dispatch(goToStep('configureFlows'))
    } else {
      // Go to previous slide
      dispatch(goToPreviousStep())
    }
  }

  const handleNext = () => {
    switch (currentStep) {
      case 'configureFlows': {
        // Check if any selected flows need triggers or schedulers
        const flowsWithTriggers = selectedFlows.filter(
          (flow: any) => flow.selected && (flow.needsTrigger || flow.needsScheduler)
        )

        if (flowsWithTriggers.length > 0) {
          // Show confirmation modal
          setShowTriggerModal(true)
        } else {
          // Proceed directly to deployment
          dispatch(completeFlowsConfiguration())
        }
        break
      }
      case 'deployment':
        // Deployment config is already saved in Redux by DeploymentSlide
        // Complete deployment and navigate to review
        dispatch(completeDeploymentConfiguration())
        break
      case 'review':
        // Submit the task
        handleSubmitTask()
        break
      default:
        break
    }
  }

  const handleContinueWithTriggers = () => {
    setShowTriggerModal(false)
    dispatch(completeFlowsConfiguration())
  }

  const handleSubmitTask = useCallback(async () => {
    if (!flowRepository) {
      BetterToast.error('Repository information is missing')
      return
    }

    if (!deploymentConfig) {
      BetterToast.error('Deployment configuration is missing')
      return
    }

    if (!onTaskSubmit) {
      BetterToast.error('Task submission handler is not available')
      return
    }

    // Build CodeContext from repository info
    let codeContext: CodeContext
    if (flowRepository.conn_type === 'S3' && flowRepository.s3TarFilePath) {
      // S3 upload
      codeContext = CodeContext.fromPartial({
        connType: 'S3',
        s3TarFilePath: flowRepository.s3TarFilePath
      })
    } else {
      // GitHub repository
      const [orgName, repoName] = parseRepositoryName(flowRepository.repository)
      codeContext = CodeContext.fromPartial({
        connType: flowRepository.conn_type,
        orgName,
        repoName,
        branchName: flowRepository.branch
      })
    }

    // Build IntegrationTestConfig with flows
    const integrationTestFlows: IntegrationTestFlow[] = selectedFlows
      .filter((f: any) => f.selected)
      .map((flow: any) => {
        const examples: IntegrationTestInputOutputExample[] =
          flow.examples?.map((ex: any) =>
            IntegrationTestInputOutputExample.fromPartial({
              inputExample: ex.input,
              outputExample: ex.output
            })
          ) || []

        return IntegrationTestFlow.fromPartial({
          flowId: flow.name,
          examples
        })
      })

    // Extract schema contents from all applications for API
    const schemaContents = applicationSchemas.flatMap(app =>
      app.schemas.map(schema => schema.content)
    )

    const integrationTestConfig = IntegrationTestConfig.fromPartial({
      flows: integrationTestFlows,
      schemas: schemaContents,
      enableComparisonTesting: autoGenerateTestData,
      enableAutoGenerateTestData: autoGenerateTestData
    })

    // Build user notes with deployment URL and additional notes
    let userNotes = ''
    if (deploymentConfig.deploymentOption === 'existing' && deploymentConfig.deploymentUrl) {
      userNotes = `app is deployed at ${deploymentConfig.deploymentUrl}`
    } else if (
      deploymentConfig.deploymentOption === 'existing' &&
      !deploymentConfig.deploymentUrl
    ) {
      userNotes = 'app is not deployed, use cloudhub to deploy and then test the app'
    }

    if (additionalNotes && additionalNotes.trim()) {
      userNotes = userNotes ? `${userNotes}\n\n${additionalNotes}` : additionalNotes
    }

    // Build runtime settings from deployment config
    const runtimeSettings = RuntimeSettings.fromPartial({
      javaVersion: deploymentConfig.javaVersion || '',
      mavenVersion: deploymentConfig.mavenVersion || '',
      muleRuntimeVersion: deploymentConfig.muleRuntimeVersion || ''
    })

    // Build command string with comma-separated flow IDs
    const flowIds = integrationTestFlows.map(f => f.flowId).join(', ')

    // Build the task params
    const taskParams: TaskParams = {
      request: AgentRequest.fromPartial({
        command: Command.fromPartial({
          commandType: CommandType.INTEGRATION_TEST,
          commandString: flowIds
        }),
        metadata: Metadata.fromPartial({
          codeContext
        }),
        runtimeSettings,
        vmArgs: deploymentConfig.deploymentOption === 'agent' ? deploymentConfig.vmArgs || '' : '',
        userNotes,
        integrationTestConfig
      })
    }

    try {
      const isSuccess = await onTaskSubmit(taskParams)
      if (isSuccess) {
        // Navigation is handled by the parent component (TaskForm/WorkspaceHomePage)
      } else {
        BetterToast.error('Failed to submit task')
      }
    } catch (error) {
      console.error('Task submission error:', error)
      BetterToast.error('Failed to submit task. Please try again.')
    }
  }, [
    flowRepository,
    deploymentConfig,
    selectedFlows,
    autoGenerateTestData,
    applicationSchemas,
    additionalNotes,
    onTaskSubmit
  ])

  const isNextButtonDisabled = () => {
    if (currentStep === 'configureFlows') {
      const hasSelectedFlows = selectedFlows.some((flow: any) => flow.selected)
      if (!hasSelectedFlows) return true

      // If auto-generate is disabled, all selected flows must have provided data
      if (!autoGenerateTestData) {
        const selectedFlowsList = selectedFlows.filter((f: any) => f.selected)
        const allHaveData = selectedFlowsList.every((f: any) => f.hasProvidedData)
        return !allHaveData
      }

      return false
    }

    if (currentStep === 'deployment') {
      // If "existing" deployment option selected, URL is required
      if (deploymentConfig?.deploymentOption === 'existing') {
        return !deploymentConfig.deploymentUrl || deploymentConfig.deploymentUrl.trim() === ''
      }
      return false
    }

    return false
  }

  if (!flowRepository) {
    return null
  }

  return (
    <div className='flex h-full flex-col bg-white'>
      {/* Main Content */}
      <main className='flex flex-1 flex-col items-center overflow-hidden'>
        <Swiper
          onSwiper={swiper => {
            swiperRef.current = swiper
          }}
          allowTouchMove={false}
          spaceBetween={0}
          slidesPerView={1}
          className='w-full h-full'
          style={{ maxWidth: '100%' }}
        >
          {/* Slide 1: Configure Flows */}
          <SwiperSlide>
            <ConfigureFlowsSlide
              repository={flowRepository.repository}
              branch={flowRepository.branch}
            />
          </SwiperSlide>

          {/* Slide 2: Deployment */}
          <SwiperSlide>
            <DeploymentSlide />
          </SwiperSlide>

          {/* Slide 3: Confirmation */}
          <SwiperSlide>
            <ConfirmationSlide />
          </SwiperSlide>
        </Swiper>
      </main>

      {/* Footer Container */}
      <footer className='flex-shrink-0 flex flex-col bg-[#FCFCFC] border-t border-[#EAEAEA]'>
        {/* Progress Bar */}
        <div className='w-full px-[32px] pt-[4px]'>
          <ProgressBar currentStep={currentStepNumber} totalSteps={totalSteps} showLabel={false} />
        </div>

        {/* 3px Gap */}
        <div className='h-[3px]' />

        {/* Footer Control */}
        <div className='flex items-center justify-between px-[32px] py-[16px]'>
          {/* Back Button - Use outlined variant on confirmation slide */}
          <Button
            variant='outlined'
            onClick={handleBack}
            className='h-[36px] rounded-[12px] px-[16px] py-[8px]'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='whitespace-nowrap'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              Back
            </Typography>
          </Button>

          {/* Next Button */}
          <Button
            variant='primary'
            onClick={handleNext}
            isDisabled={isNextButtonDisabled()}
            className='h-[36px] rounded-[12px] bg-[#262626] px-[16px] py-[8px] hover:bg-[#383838] disabled:bg-[rgba(0,0,0,0.12)]'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='whitespace-nowrap text-white'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              Next
            </Typography>
          </Button>
        </div>
      </footer>

      {/* Trigger Confirmation Modal */}
      <TriggerConfirmationModal
        isOpen={showTriggerModal}
        onClose={() => setShowTriggerModal(false)}
        onContinue={handleContinueWithTriggers}
      />
    </div>
  )
}

export default IntegrationTestFlowScreen
